import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Bot,
  Calendar,
  Star,
  Target,
  Trophy,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import PhoneMockup from "../components/ui/PhoneMockup";
import SEO, { generateBreadcrumbSchema, generateWebPageSchema } from "../components/seo/SEO";
import { useAppDownload } from "../context/AppDownloadContext";
import home1Screen    from "../assets/images/home1.svg";
import home2Screen    from "../assets/images/headerimage/home.svg";
import outlinerScreen from "../assets/images/headerimage/outliner.svg";
import examprep from "../assets/images/examprep.svg";

// ─── Data ─────────────────────────────────────────────────────────────────────

const preUniFeatures = [
  { icon: BookOpen, title: "JAMB Past Questions" },
  { icon: Target, title: "WAEC Practice" },
  { icon: Bot, title: "CBT Simulator" },
  { icon: Calendar, title: "AI Study Planner" },
  { icon: Trophy, title: "Challenges & Streaks" },
  { icon: Star, title: "Eligibility Checker" },
];

const universityFeatures = [
  { title: "Digital Student ID", description: "Verified campus identity: matric number, department, level, and semester always in your pocket." },
  { title: "GPA Tracker", description: "Track your CGPA across every semester. Know exactly where you stand academically at all times." },
  { title: "Alumni Mentorship", description: "Connect with graduates working in your field for advice, referrals, and real career guidance." },
  { title: "Campus Safety", description: "Emergency contacts and safety resources, always one tap away. A companion that keeps you safe too." },
  { title: "Academic Calendar", description: "Lectures, deadlines, exams, and campus events in one Teams-style calendar with course rep sync." },
  { title: "Course Group Chats", description: "Dedicated groups by course, year, or set with course rep admin and timetable announcement sync." },
  { title: "AI Writing Suite", description: "Grammar checker, paraphraser, translator, essay writer, summariser: all academic AI needs in one place." },
  { title: "AI Question Scanner", description: "Snap a photo of any equation or question and get step-by-step solutions and explanations instantly." },
  { title: "Outline-Based Learning", description: "Upload course outlines and get auto-generated flashcards, quizzes, and summaries tailored to your exams." },
  { title: "Internship Discovery", description: "Find and apply for internships filtered by department, duration, and location. Track your applications." },
  { title: "Hostel Booking", description: "Discover and book hostel accommodation with detailed listings, photos, and availability calendars." },
  { title: "Scholarship Alerts", description: "Get notified about scholarships and bursaries matching your profile, course, and academic standing." },
];

const institutionBenefits = [
  { title: "Student Engagement Analytics", description: "Track student activity, study patterns, and engagement metrics across your institution in real-time dashboards." },
  { title: "Official Communication Channel", description: "Reach students through verified institutional channels. Announcements sync directly to student timetables." },
  { title: "Course Content Integration", description: "Upload official course outlines and materials. Students get auto-generated study aids aligned with your curriculum." },
  { title: "Hostel Management", description: "Integrate hostel inventory and booking directly into the student app. Streamline accommodation allocation." },
  { title: "Career Services Integration", description: "Post internships and opportunities directly to relevant students. Track applications and placements." },
  { title: "Verified Student Identity", description: "Digital student ID integration for secure verification at events, libraries, and campus facilities." },
];

const useCases = [
  {
    title: "Universities",
    description: "Adopt CampusGenie as your official student app, a unified experience for academic life, campus services, and communication.",
    features: ["Branded instance with your colours and logo", "Integration with student information systems", "Official announcement and timetable channels", "Analytics dashboard for student affairs"],
  },
  {
    title: "Secondary Schools",
    description: "Help your students prepare for JAMB and WAEC with a dedicated prep platform that transitions them smoothly to university.",
    features: ["School-wide progress tracking", "Class-based leaderboards and challenges", "Teacher oversight dashboard", "Parent progress reports"],
  },
  {
    title: "EdTech Partners",
    description: "Integrate your content and services into the CampusGenie ecosystem to reach thousands of engaged students.",
    features: ["Content partnership opportunities", "API integration options", "Co-branded features", "Revenue sharing models"],
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "Free",
    description: "For schools exploring CampusGenie",
    features: ["Up to 500 students", "Basic analytics dashboard", "Standard support", "Community features"],
    cta: "Get Started",
    featured: false,
  },
  {
    name: "Institution",
    price: "Custom",
    description: "For universities and large schools",
    features: ["Unlimited students", "Full analytics suite", "Priority support", "Custom branding", "API access", "Dedicated success manager"],
    cta: "Contact Sales",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "For multi-campus deployments",
    features: ["Everything in Institution", "Multi-campus support", "SIS integration", "Custom development", "On-premise option", "SLA guarantee"],
    cta: "Contact Sales",
    featured: false,
  },
];

const roadmap = [
  { phase: "Phase 1", title: "Bridge & Core Loop",      status: "Live",        items: ["Bridge experience", "News & Feed", "Study Planner", "AI Assistant (3 tools)"] },
  { phase: "Phase 2", title: "Community & Authority",   status: "Live", items: ["Course Group Chats", "Timetable Integration", "More AI Tools", "Events Calendar"] },
  { phase: "Phase 3", title: "Learning from Outline",   status: "Live",     items: ["Outline Upload", "Generated Flashcards", "Auto Quizzes", "E-Library MVP"] },
  { phase: "Phase 4", title: "Career & Life",           status: "Live",     items: ["Internships", "AI Career Chatbot", "Question Scanner", "Hostel Booking"] },
];

const FLOAT_EASE = "easeInOut";

// ─── Audience tab nav ─────────────────────────────────────────────────────────
const TABS = [
  { id: "preuni",       label: "Pre-University",  anchor: "#pre-university" },
  { id: "university",   label: "University Life",  anchor: "#university" },
  { id: "institutions", label: "For Institutions", anchor: "#institutions" },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function ProductPage() {
  const { openAppModal } = useAppDownload();
  const [activeUniFeature, setActiveUniFeature] = useState(0);

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Product", url: "/product" },
  ];

  const structuredData = [
    generateWebPageSchema({
      title: "Product · CampusGenie",
      description: "One platform covering JAMB & WAEC exam prep, university academic tools, Digital Student ID, GPA Tracker, Alumni Mentorship, and institutional solutions.",
      url: "/product",
    }),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  const scrollTo = (anchor) => {
    const el = document.querySelector(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <SEO
        title="Product · Exam Prep, University Tools & Institutional Solutions"
        description="CampusGenie covers every stage: JAMB & WAEC exam prep, university academic tools (Digital Student ID, GPA Tracker, Alumni Mentorship), AI writing suite, campus life, and white-label institutional solutions."
        url="/product"
        structuredData={structuredData}
      />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-grid">
            <Reveal>
              <div>
                <span className="eyebrow">The Platform</span>
                <h1 className="heading-hero" style={{ maxWidth: "16ch" }}>
                  One platform for every stage of the student journey.
                </h1>
                <p className="body-large" style={{ maxWidth: "520px", marginBottom: "32px" }}>
                  Whether you're preparing for JAMB, navigating university life, or an institution looking to empower your students  CampusGenie has a place for you.
                </p>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                    Download the App
                  </button>
                  <Link to="/contact" className="btn btn-secondary btn-lg">
                    Schedule a Demo
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="product-hero-visual">
                <div className="showcase-glow" style={{ top: "30%", left: "30%" }} />
                {/* Back phone for depth */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 0.5, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.7 }}
                  style={{ position: "absolute", right: "-5%", top: "10%", transform: "rotate(6deg) scale(0.8)", zIndex: 0, pointerEvents: "none" }}
                >
                  <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: FLOAT_EASE, delay: 1 }}>
                    <PhoneMockup image={outlinerScreen} alt="AI Writing Tools" size="default" />
                  </motion.div>
                </motion.div>
                {/* Main phone */}
                <div style={{ position: "relative", zIndex: 2 }}>
                  <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 7, repeat: Infinity, ease: FLOAT_EASE }}>
                    <PhoneMockup image={home1Screen} alt="CampusGenie Home" size="large" />
                  </motion.div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Audience Jump Nav ────────────────────────────────────────────── */}
      <section className="section" style={{ paddingTop: "48px", paddingBottom: "48px" }}>
        <div className="container">
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <span className="eyebrow">Jump to your section</span>
            </div>
            <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => scrollTo(tab.anchor)}
                  className="btn btn-secondary"
                  style={{ fontWeight: 600 }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── PRE-UNIVERSITY ───────────────────────────────────────────────── */}
      <section id="pre-university" className="section section-alt" style={{ scrollMarginTop: "88px" }}>
        <div className="container">
          <div className="showcase-grid">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">Pre-University</span>
                <h2 className="heading-2">
                  Ace JAMB & WAEC with tools built for serious practice.
                </h2>
                <p className="body-large">
                  Our exam prep suite gives you everything needed to score high, secure admission, and walk into exam day with confidence.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">10+ years of JAMB & WAEC past questions by subject and year</div>
                  <div className="showcase-list-item">Pixel-perfect CBT simulator that mirrors the real exam interface</div>
                  <div className="showcase-list-item">AI study planner that adapts to your weak areas daily</div>
                  <div className="showcase-list-item">Streaks, leaderboards, and challenges to keep you motivated</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="showcase-phone-wrap">
                <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6.5, repeat: Infinity, ease: FLOAT_EASE }}>
                  <PhoneMockup image={examprep} alt="Study Tab" size="large" />
                </motion.div>
              </div>
            </Reveal>
          </div>

          <div className="feature-strip">
            {preUniFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Reveal key={feature.title} delay={index * 0.04}>
                  <div className="feature-strip-item">
                    <Icon size={22} strokeWidth={1.75} aria-hidden />
                    <span>{feature.title}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── UNIVERSITY LIFE ──────────────────────────────────────────────── */}
      <section id="university" className="section section-dark" style={{ scrollMarginTop: "88px" }}>
        <div className="container">
          <div className="showcase-grid showcase-grid-reverse">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">University Life</span>
                <h2 className="heading-2" style={{ color: "white" }}>
                  Your complete university OS.
                </h2>
                <p className="body-large">
                  After admission, CampusGenie becomes your academic companion, from Digital Student ID and GPA tracking to alumni mentorship, group chats, and AI tools.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">Digital Student ID  verified campus identity always in your pocket</div>
                  <div className="showcase-list-item">GPA Tracker  monitor your CGPA every semester at a glance</div>
                  <div className="showcase-list-item">Alumni Mentorship  connect with graduates for career guidance</div>
                  <div className="showcase-list-item">Campus Safety, Scholarship Alerts, Academic Calendar, and more</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="showcase-phone-wrap showcase-phone-wrap--align-start showcase-phone-wrap--shift">
                <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: FLOAT_EASE, delay: 0.5 }}>
                  <PhoneMockup image={home2Screen} alt="Community Tab" size="large" />
                </motion.div>
              </div>
            </Reveal>
          </div>

          <div className="capability-spotlight">
            <div className="capability-spotlight-panel" aria-live="polite">
              <AnimatePresence mode="wait">
                <motion.div
                  key={universityFeatures[activeUniFeature].title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28 }}
                >
                  <span className="capability-spotlight-index">
                    {String(activeUniFeature + 1).padStart(2, "0")} / {String(universityFeatures.length).padStart(2, "0")}
                  </span>
                  <h3 className="capability-spotlight-title">{universityFeatures[activeUniFeature].title}</h3>
                  <p className="capability-spotlight-description">{universityFeatures[activeUniFeature].description}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="capability-chip-cloud" role="tablist" aria-label="University capabilities">
              {universityFeatures.map((feature, index) => (
                <button
                  key={feature.title}
                  type="button"
                  role="tab"
                  aria-selected={activeUniFeature === index}
                  className={`capability-chip${activeUniFeature === index ? " is-active" : ""}`}
                  onClick={() => setActiveUniFeature(index)}
                >
                  {feature.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Student testimonials ─────────────────────────────────────────── */}
      <section className="section social-proof-section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Student Stories</span>
              <h2 className="heading-2">Trusted by students across Nigeria.</h2>
            </div>
          </Reveal>

          <div className="testimonials-grid">
            {[
              { quote: "CampusGenie helped me score 298 in JAMB. The CBT practice was exactly like the real exam, and the AI study planner kept me on track the whole time.", author: "Chisom A.", role: "Computer Science · 100L · UNILAG", avatar: "C" },
              { quote: "The GPA tracker is something I didn't know I needed. I can see my CGPA every semester without calculating manually. And the Digital Student ID saved me at the library gate.", author: "Emeka O.", role: "Engineering · 300L · OAU", avatar: "E" },
              { quote: "The Alumni network connected me to two internship leads within a week. I wasn't expecting that from a study app. CampusGenie is built for everything.", author: "Fatima M.", role: "Law · 400L · ABU Zaria", avatar: "F" },
            ].map((t, i) => (
              <Reveal key={t.author} delay={i * 0.1}>
                <div className="testimonial-card">
                  <p className="testimonial-quote">"{t.quote}"</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">{t.avatar}</div>
                    <div>
                      <div className="testimonial-name">{t.author}</div>
                      <div className="testimonial-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR INSTITUTIONS ─────────────────────────────────────────────── */}
      <section id="institutions" className="section section-dark" style={{ scrollMarginTop: "88px" }}>
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">For Institutions</span>
              <h2 className="heading-2" style={{ color: "white" }}>
                Empower your students with a world-class platform.
              </h2>
              <p className="body-large">
                Partner with CampusGenie to provide comprehensive academic support, streamline campus operations, and gain real-time insight into student engagement.
              </p>
            </div>
          </Reveal>

          <div className="institution-split" style={{ marginTop: "56px" }}>
            {institutionBenefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={index * 0.05}>
                <div className="institution-split-row">
                  <h3 className="institution-split-title">{benefit.title}</h3>
                  <p className="institution-split-description">{benefit.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Use Cases</span>
              <h2 className="heading-2">Tailored for every institution type.</h2>
            </div>
          </Reveal>

          <div className="grid grid-3">
            {useCases.map((uc, index) => (
              <Reveal key={uc.title} delay={index * 0.1}>
                <div className="feature-card" style={{ height: "100%" }}>
                  <h3 className="heading-4" style={{ marginBottom: "12px" }}>{uc.title}</h3>
                  <p className="body-small" style={{ marginBottom: "20px" }}>{uc.description}</p>
                  <ul style={{ margin: 0 }}>
                    {uc.features.map((f) => (
                      <li key={f} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0", fontSize: "14px", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border-subtle)" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Institution Stats */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="stats-grid">
              <div className="stat-card"><div className="stat-value">1K+</div><div className="stat-label">Students using CampusGenie today</div></div>
              <div className="stat-card"><div className="stat-value">4–7x</div><div className="stat-label">Higher retention than typical apps</div></div>
              <div className="stat-card"><div className="stat-value">90%</div><div className="stat-label">Student satisfaction rate</div></div>
              <div className="stat-card"><div className="stat-value">24/7</div><div className="stat-label">AI support availability</div></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section className="section section-alt pricing-section" id="pricing">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Pricing</span>
              <h2 className="heading-2">Plans that scale with your institution.</h2>
              <p className="body-large">Start free and upgrade as you grow. All plans include core features.</p>
            </div>
          </Reveal>

          <div className="grid grid-3">
            {pricingPlans.map((plan, index) => (
              <Reveal key={plan.name} delay={index * 0.1}>
                <div className={`pricing-card ${plan.featured ? "featured" : ""}`}>
                  {plan.featured && <div className="pricing-badge">Most Popular</div>}
                  <h3 className="heading-4">{plan.name}</h3>
                  <div className="pricing-price">
                    {plan.price}
                    {plan.price !== "Free" && plan.price !== "Custom" && <span>/mo</span>}
                  </div>
                  <p className="body-small">{plan.description}</p>
                  <div className="pricing-features">
                    {plan.features.map((f) => (
                      <div key={f} className="pricing-feature">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                        {f}
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className={`btn ${plan.featured ? "btn-primary" : "btn-secondary"}`} style={{ width: "100%" }}>
                    {plan.cta}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Implementation</span>
              <h2 className="heading-2">Getting started is simple.</h2>
            </div>
          </Reveal>

          <div className="grid grid-4">
            {[
              { step: "01", title: "Discovery Call",   desc: "We learn about your institution's needs and goals." },
              { step: "02", title: "Custom Setup",     desc: "We configure CampusGenie for your institution." },
              { step: "03", title: "Pilot Launch",     desc: "Roll out to a pilot group of students and staff." },
              { step: "04", title: "Full Deployment",  desc: "Scale across your entire institution." },
            ].map((item, index) => (
              <Reveal key={item.step} delay={index * 0.1}>
                <div className="feature-card" style={{ textAlign: "center" }}>
                  <div style={{ width: 48, height: 48, background: "var(--gradient-primary)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, margin: "0 auto 16px" }}>
                    {item.step}
                  </div>
                  <h4 className="heading-4">{item.title}</h4>
                  <p className="body-small">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roadmap ──────────────────────────────────────────────────────── */}
      <section className="section section-alt" id="roadmap">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Product Roadmap</span>
              <h2 className="heading-2">Building the future of student success.</h2>
              <p className="body-large">Our phased approach delivers value at every stage while building toward a complete platform.</p>
            </div>
          </Reveal>

          <div className="grid grid-4">
            {roadmap.map((phase, index) => (
              <Reveal key={phase.phase} delay={index * 0.1}>
                <div className="feature-card" style={{ height: "100%" }}>
                  <div className="badge" style={{
                    marginBottom: "16px",
                    background: phase.status === "Live" ? "var(--color-primary)" : phase.status === "In Progress" ? "var(--color-accent)" : "var(--color-border)",
                    color: phase.status === "Live" || phase.status === "In Progress" ? "white" : "var(--color-text-secondary)",
                  }}>
                    {phase.status}
                  </div>
                  <div className="feature-number">{phase.phase}</div>
                  <h4 className="heading-4">{phase.title}</h4>
                  <ul style={{ marginTop: "16px" }}>
                    {phase.items.map((item) => (
                      <li key={item} style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border-subtle)", fontSize: "14px", color: "var(--color-text-secondary)" }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="cta-section">
              <div className="cta-content">
                <h2 className="heading-2">
                  Ready to get started?
                </h2>
                <p className="body-large">
                  Download CampusGenie as a student, or schedule a demo if you're an institution looking to partner.
                </p>
                <div className="cta-buttons">
                  <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                    Download the App
                  </button>
                  <Link to="/contact" className="btn btn-secondary btn-lg">
                    Schedule a Demo
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
